import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import PropTypes from 'prop-types';

import {
	getContactsToRemove,
	ignoreContactToRemove,
	removePotentialContact
} from '../../../../../../../actions/ContactDBAccessLog';
import {labels, currentResource} from '../../../../../../../util/utils';
import {concurrencyErrorCode, inventedConcurrencyErrorCode} from '../../../../../../../util/uiconstants';
import isContactListEmpty from '../isContactListEmpty';
import {raiseJSValidationError} from '../../../../../../../actions/erroractions';
import {updateDisplayData} from '../../../../../../../actions/Metrics/dashboardContextAction';

import {IconoirProhibition, IconoirTrash} from '@ey-xd/motif-icon';

import BorderBox from '../../../../../../Common/BorderBox';
import UserList from '../UserList';
import normalizeRemoveAllContacts from './normalizeRemoveAllContacts';
import Button from '../../../../../../CoreWrappers/Button';

const ContactsToRemove = ({setIsLoading, renderEmpty = null}) => {
	const contactsToRemove = useSelector(({contactsAccessLog}) => contactsAccessLog.contactsToRemove);
	const projectId = useSelector(({project}) => project?.projectId);
	const orgChart = useSelector(({dashboardContext}) => dashboardContext?.orgChart);
	const entities = useSelector(({dashboardContext}) => dashboardContext?.entities);
	const engagements = useSelector(({dashboardContext}) => dashboardContext?.engagements);

	const dispatch = useDispatch();

	const displayCustomError = (error, users) => {
		setIsLoading(false);
		const errorCode = error?.response?.data?.errors?.Error[0]?.split('-')[0].trim() || '';
		if (errorCode == concurrencyErrorCode) {
			dispatch(
				raiseJSValidationError(
					inventedConcurrencyErrorCode +
						' - ' +
						currentResource.Errors[errorCode].replace('{0}', users?.name + ' ' + users?.lastName)
				)
			);
		}
	};

	useEffect(() => {
		if (contactsToRemove === null) {
			setIsLoading(true);
			dispatch(getContactsToRemove(projectId, orgChart))
				.then(() => setIsLoading(false))
				.catch(() => setIsLoading(false));
		}
	}, [contactsToRemove]);

	const handleIgnore = (user, entityId) => {
		setIsLoading(true);
		dispatch(ignoreContactToRemove(projectId, user, entityId))
			.then(() => setIsLoading(false))
			.catch((error) => displayCustomError(error, user));
	};

	const handleRemove = (user, entityId) => {
		setIsLoading(true);
		let newEntities = [...entities];
		dispatch(removePotentialContact(projectId, user, entityId))
			.then(() => {
				newEntities = normalizeRemoveAllContacts(newEntities, [user], entityId);
				dispatch(updateDisplayData(engagements, newEntities, orgChart));
				setIsLoading(false);
			})
			.catch((error) => displayCustomError(error, user));
	};

	if (contactsToRemove === null) return null;
	if (isContactListEmpty(contactsToRemove) && renderEmpty) return renderEmpty(labels.contactsToRemoveEmpty);

	const renderControls = (userId, entityId) => {
		const removeProps = {
			svg: IconoirTrash,
			title: labels.remove,
			onClick: () => handleRemove(userId, entityId),
			variant: 'ghost',
			iconSize: 20
		};
		const ignoreProps = {
			svg: IconoirProhibition,
			title: labels.ignore,
			onClick: () => handleIgnore(userId, entityId),
			variant: 'ghost',
			iconSize: 20
		};
		return (
			<>
				<Button {...removeProps} />
				<Button {...ignoreProps} />
			</>
		);
	};

	const props = {
		entityList: contactsToRemove,
		renderControls
	};

	return (
		<BorderBox className="list-wrapper">
			<UserList {...props} />
		</BorderBox>
	);
};

ContactsToRemove.propTypes = {
	setIsLoading: PropTypes.func.isRequired,
	renderEmpty: PropTypes.func
};

ContactsToRemove.defaultProps = {};

export default ContactsToRemove;
